import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Identity-stable trampoline for an event-handler callback. The
 * returned function never changes reference; the body always reaches
 * the latest closure via a ref that's mirrored in {@link
 * useLayoutEffect} (so handlers fired between renders see fresh state).
 * Mirrors the React {@code useEffectEvent} RFC.
 *
 * <p>Only safe for callbacks invoked AFTER render (clicks, async
 * writes). Render-time derivations need plain {@code useCallback} so
 * memoised consumers re-render when the closure flips.</p>
 */
export function useStableCallback<T extends (...args: never[]) => unknown>(
  fn: T,
): T {
  const ref = useRef<T>(fn);
  useLayoutEffect(() => {
    ref.current = fn;
  });
  const stable = useCallback((...args: Parameters<T>): ReturnType<T> => {
    return ref.current(...args) as ReturnType<T>;
  }, []);
  return stable as T;
}
